import React, { useEffect, useRef, useState } from 'react';
import Layout from '../components/layout/Layout';
import { useFamilyStore } from '../stores/familyStore';
import { useAuthStore } from '../stores/authStore';
import { cn } from '../utils/cn';
import { supabase } from '../lib/supabase';
import type { ActivityLogEntry } from '../types/ActivityLog';
import { useApiService } from '../services/api';
import { ActivityLogList } from '../components/family/ActivityLogList';
import { usePermissions } from '../hooks/usePermissions';
import { useEntitlements } from '../hooks/useEntitlements';

type AccountTab = 'plan' | 'security' | 'history' | 'reports' | 'account';

/** Escapes user-supplied strings for safe HTML interpolation. */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');
}

const Account: React.FC = () => {
  const { user } = useAuthStore();
  const { family, isLoading: familyLoading, initialize } = useFamilyStore();
  const api = useApiService();
  const { isObserver } = usePermissions();

  const [activeTab, setActiveTab] = useState<AccountTab>(() => {
    const saved = sessionStorage.getItem('account-active-tab');
    return (saved as AccountTab) || 'plan';
  });

  // Password change form state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  // Activity log state
  const [activityLogs, setActivityLogs] = useState<ActivityLogEntry[]>([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [logsError, setLogsError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportMonth, setExportMonth] = useState<string>(new Date().toISOString().slice(0,7));
  const [historyPlan, setHistoryPlan] = useState<'basic'|'pro'|'max'|null>(null);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyTotal, setHistoryTotal] = useState(0);
  const pageSize = 25;
  const [actionFilter, setActionFilter] = useState<string>('');
  const [reportMonth, setReportMonth] = useState<string>(new Date().toISOString().slice(0,7));
  const [careReportMonth, setCareReportMonth] = useState<string>(new Date().toISOString().slice(0,7));

  // Delete account state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Plan from centralized hook
  const { planId, status: planStatus } = useEntitlements(family?.id);

  const initRef = useRef(false);

  useEffect(() => {
    if (user && !family && !familyLoading && !initRef.current) {
      initRef.current = true;
      initialize();
    }
  }, [user, family, familyLoading, initialize]);

  useEffect(() => {
    if (isObserver && activeTab !== 'security') {
      setActiveTab('security');
    }
  }, [isObserver, activeTab]);

  useEffect(() => {
    sessionStorage.setItem('account-active-tab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'history' && family) {
      // Resetuj stronę przy wejściu na zakładkę lub zmianie filtra
      setHistoryPage(0);
      void fetchActivityLogs(0);
    }
  }, [activeTab, family, actionFilter]);

  // Persist/restore miesiące raportów/eksportów
  useEffect(() => {
    try {
      const rm = localStorage.getItem('reportMonth');
      if (rm && /^\d{4}-\d{2}$/.test(rm)) setReportMonth(rm);
    } catch {}
    try {
      const em = localStorage.getItem('exportMonth');
      if (em && /^\d{4}-\d{2}$/.test(em)) setExportMonth(em);
    } catch {}
    try {
      const cm = localStorage.getItem('careReportMonth');
      if (cm && /^\d{4}-\d{2}$/.test(cm)) setCareReportMonth(cm);
    } catch {}
  }, []);

  useEffect(() => {
    try { localStorage.setItem('reportMonth', reportMonth); } catch {}
  }, [reportMonth]);

  useEffect(() => {
    try { localStorage.setItem('exportMonth', exportMonth); } catch {}
  }, [exportMonth]);

  useEffect(() => {
    try { localStorage.setItem('careReportMonth', careReportMonth); } catch {}
  }, [careReportMonth]);

  const fetchActivityLogs = async (page: number) => {
    if (!family || !user) return;
    setIsLoadingLogs(true);
    setLogsError(null);
    try {
      // Użyj planu z hooka useEntitlements (bez dodatkowego requestu)
      const plan = planId;
      setHistoryPlan(plan);

      // Zbuduj zapytanie z limitami
      let query = supabase
        .from('recent_family_activity')
        .select('*', { count: 'exact' })
        .eq('family_id', family.id)
        .order('created_at', { ascending: false });

      if (actionFilter) {
        query = query.eq('action', actionFilter);
      }

      if (plan === 'basic') {
        // Basic: tylko 10 ostatnich bez paginacji
        const { data, count, error } = await query.limit(10);
        if (error) throw error as any;
        setActivityLogs(data || []);
        setHistoryTotal(Math.min(count || (data?.length || 0), 10));
        setHistoryPage(0);
      } else {
        // Pro: 30 dni historii, Max: 180 dni historii; 25 wpisów na stronę
        const days = plan === 'pro' ? 30 : 180;
        const fromDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
        query = query.gte('created_at', fromDate);
        const from = page * pageSize;
        const to = from + pageSize - 1;
        const { data, count, error } = await query.range(from, to);
        if (error) throw error as any;
      setActivityLogs(data || []);
        setHistoryTotal(count || 0);
        setHistoryPage(page);
      }
    } catch (err: any) {
      setLogsError(err.message || 'Wystąpił błąd podczas pobierania historii aktywności');
    } finally {
      setIsLoadingLogs(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Wszystkie pola są wymagane');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Nowe hasła nie są identyczne');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('Nowe hasło musi mieć co najmniej 8 znaków');
      return;
    }
    setIsChangingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      alert('Hasło zostało zmienione pomyślnie!');
    } catch (error: any) {
      setPasswordError(error.message || 'Wystąpił błąd podczas zmiany hasła');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleExportData = async () => {
    if (!family || !user) {
      alert('Brak danych rodziny lub użytkownika.');
      return;
    }
    setIsExporting(true);
    try {
      const familyId = family.id;

      // Zakres miesiąca dla eksportu
      const monthStr = exportMonth;
      const [y, m] = monthStr.split('-').map(Number);
      const start = new Date(y, m - 1, 1);
      const end = new Date(y, m, 0, 23, 59, 59, 999);
      const startIso = start.toISOString().slice(0,10); // YYYY-MM-DD dla calendar_entries.date
      const endIso = end.toISOString().slice(0,10);

      const [familyRes, childrenRes, membersRes, calendarRes, eventsRes] = await Promise.all([
        supabase.from('families').select('*').eq('id', familyId).single(),
        supabase.from('children').select('*').eq('family_id', familyId),
        supabase.from('family_members').select('*').eq('family_id', familyId),
        supabase
          .from('calendar_entries')
          .select('*')
          .eq('family_id', familyId)
          .gte('date', startIso)
          .lte('date', endIso),
        supabase
          .from('events')
          .select('*')
          .eq('family_id', familyId)
          .gte('start_at', new Date(y, m - 1, 1).toISOString())
          .lte('start_at', end.toISOString())
      ]);

      const exportPayload = {
        exported_at: new Date().toISOString(),
        month: monthStr,
        family: familyRes.data || null,
        children: childrenRes.data || [],
        members: membersRes.data || [],
        calendar_entries: calendarRes.data || [],
        events: eventsRes.data || []
      };

      const blob = new Blob([JSON.stringify(exportPayload, null, 2)], { type: 'application/json;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `2domy-export-${familyId}-${monthStr}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('❌ Błąd eksportu danych:', err);
      alert('Nie udało się wyeksportować danych. Spróbuj ponownie.');
    } finally {
      setIsExporting(false);
    }
  };

  // removed PDF generation function

  const handlePrintExpenseReport = async () => {
    if (!family || !user) {
      alert('Zaloguj się i wybierz rodzinę.');
      return;
    }
    const monthStr = reportMonth;
    if (!monthStr || !/^\d{4}-\d{2}$/.test(monthStr)) return;
    const [year, month] = monthStr.split('-').map(Number);
    const start = new Date(year, month - 1, 1);
    const end = new Date(year, month, 0, 23, 59, 59, 999);

    // Otwórz okno NATYCHMIAST po kliknięciu (iOS wymaga synchronizacji z gestem)
    const preview = window.open('', '_blank');
    if (preview) {
      preview.document.open();
      preview.document.write(`<!doctype html><html lang="pl"><head><meta charset="utf-8" /><meta name="viewport" content="width=device-width, initial-scale=1" /><title>Generowanie…</title></head><body style="font-family: system-ui, -apple-system, Segoe UI, Roboto, sans-serif; padding: 16px; color:#0f172a;"><div>Generowanie raportu kosztów — ${monthStr}…</div></body></html>`);
      preview.document.close();
    }

    try {
      const membersRes = await api.getFamilyMembers(family.id);
      const parents = (membersRes.members || []).filter(m => m.role === 'parent_a' || m.role === 'parent_b');
      const parentA = parents.find(p => p.role === 'parent_a');
      const parentB = parents.find(p => p.role === 'parent_b');
      const userIdToEmail = new Map<string, string>();
      (membersRes.members || []).forEach(m => { if (m.user_id) userIdToEmail.set(m.user_id, m.user_email); });

      const { data: childrenRes } = await supabase
        .from('children')
        .select('id,name')
        .eq('family_id', family.id);
      const childIdToName = new Map<string, string>((childrenRes || []).map((c: any) => [c.id, c.name]));

      const { data: expenses, error } = await supabase
        .from('events')
        .select('*')
        .eq('family_id', family.id)
        .eq('type', 'expense')
        .gte('start_at', start.toISOString())
        .lte('start_at', end.toISOString())
        .order('start_at');
      if (error) throw error;

      const items = (expenses || []).map((e: any) => ({
        date: new Date(e.start_at).toISOString().slice(0,10),
        title: e.title || 'Wydatek',
        amount: typeof e.amount === 'number' ? e.amount : 0,
        currency: e.currency || 'PLN',
        childName: childIdToName.get(e.child_id) || '—',
        parentEmail: userIdToEmail.get(e.created_by) || '—',
      }));

      const byParent = new Map<string, typeof items>();
      for (const it of items) {
        const key = it.parentEmail || '—';
        if (!byParent.has(key)) byParent.set(key, []);
        byParent.get(key)!.push(it);
      }

      const title = `Raport kosztów — ${monthStr}`;
      const stamp = new Date().toLocaleString('pl-PL');
      const email = user?.email || '—';

      const styles = `
        <style>
          @page { margin: 16mm; }
          body { font-family: 'Noto Sans', system-ui, -apple-system, Segoe UI, Roboto, sans-serif; color: #0f172a; font-size: 14px; line-height: 1.5; }
          h1 { font-size: 22px; margin: 0 0 10px; }
          .meta { font-size: 14px; margin: 0 0 8px; }
          .section { margin: 18px 0; }
          .section h2 { font-size: 16px; margin: 0 0 10px; }
          table { width: 100%; border-collapse: collapse; font-size: 14px; }
          th, td { border: 1px solid #e2e8f0; padding: 8px 10px; text-align: left; }
          th { background: #e9f0ff; }
          .child { margin-bottom: 16px; }
          .child thead th { background: #f1f5f9; }
          .footer { position: fixed; bottom: 10mm; left: 16mm; right: 16mm; font-size: 11px; color: #475569; }
        </style>
      `;

      let sectionsHtml = '';
      let totalsByCurrency = new Map<string, number>();
      
      for (const [parent, list] of byParent.entries()) {
        const rows = list.map(it => `
          <tr>
            <td>${it.date}</td>
            <td>${escapeHtml(it.childName)}</td>
            <td>${escapeHtml(it.title)}</td>
            <td>${it.amount.toFixed(2)}</td>
            <td>${escapeHtml(it.currency)}</td>
          </tr>
        `).join('');
        
        // Grupuj koszty według walut dla każdego rodzica
        const parentTotalsByCurrency = new Map<string, number>();
        for (const item of list) {
          const currency = item.currency || 'PLN';
          const currentTotal = parentTotalsByCurrency.get(currency) || 0;
          parentTotalsByCurrency.set(currency, currentTotal + (typeof item.amount === 'number' ? item.amount : 0));
          
          // Dodaj do sumy globalnej dla tej waluty
          const globalTotal = totalsByCurrency.get(currency) || 0;
          totalsByCurrency.set(currency, globalTotal + (typeof item.amount === 'number' ? item.amount : 0));
        }
        
        // Generuj podsumowanie dla każdej waluty rodzica
        let parentTotalsHtml = '';
        for (const [currency, total] of parentTotalsByCurrency.entries()) {
          parentTotalsHtml += `<tr><td colspan="3">Suma (${escapeHtml(parent)}) - ${escapeHtml(currency)}</td><td>${total.toFixed(2)}</td><td>${escapeHtml(currency)}</td></tr>`;
        }
        
        sectionsHtml += `
          <div class="section">
            <h2>Rodzic: ${escapeHtml(parent)}</h2>
            ${list.length === 0 ? '<div class="meta">Brak kosztów w tym miesiącu.</div>' : `
            <table>
              <thead>
                <tr><th>Data</th><th>Dziecko</th><th>Tytuł</th><th>Kwota</th><th>Waluta</th></tr>
              </thead>
              <tbody>${rows}</tbody>
              <tfoot>${parentTotalsHtml}</tfoot>
            </table>`}
          </div>
        `;
      }

      const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) || (navigator.maxTouchPoints || 0) > 1;
      const html = `
        <!doctype html>
        <html lang="pl">
          <head>
            <meta charset="utf-8" />
            <meta name="viewport" content="width=device-width, initial-scale=1" />
            <link href="https://fonts.googleapis.com/css2?family=Noto+Sans:wght@400;600&display=swap&subset=latin-ext" rel="stylesheet" />
            ${styles}
            <title>${title}</title>
          </head>
          <body>
            <div style="display:flex; align-items:center; justify-content:space-between; gap:12px;">
              <div>
                <h1>${title}</h1>
                <div class="meta"><strong>Rodzic A</strong>: ${escapeHtml(parentA?.user_email || '—')}</div>
                <div class="meta"><strong>Rodzic B</strong>: ${escapeHtml(parentB?.user_email || '—')}</div>
              </div>
              <div style="display:flex; gap:8px; align-items:center;">
                <button id="closeBtn" style="padding:8px 12px; font-size:12px; background:#fff; color:#1e293b; border:1px solid #cbd5e1; border-radius:6px;">Zamknij</button>
                <button id="printBtn" style="display:${isMobile ? 'inline-block' : 'none'}; padding:8px 12px; font-size:12px; background:#1e293b; color:#fff; border:0; border-radius:6px;">Drukuj</button>
              </div>
            </div>
            ${sectionsHtml}
            <div class="section" style="margin-top:24px;">
              <h2>Podsumowanie łączne według walut:</h2>
              ${Array.from(totalsByCurrency.entries()).map(([currency, total]) =>
                `<div style="font-size: 13px; font-weight:600; margin: 8px 0;">${escapeHtml(currency)}: ${total.toFixed(2)}</div>`
              ).join('')}
            </div>
            <div class="footer">Raport wygenerowany z aplikacji 2domy.pl — konto: ${escapeHtml(email)} — ${stamp}</div>
            <script>
              (function(){
                var isMobile = ${isMobile ? 'true' : 'false'};
                function goPrint(){ try { window.print(); } catch(e){} }
                function goClose(){ try { window.close(); setTimeout(function(){ if (!document.hidden) { window.location.href = '/account'; } }, 50); } catch(e){ try { window.location.href = '/account'; } catch(_){} } }
                if (!isMobile) {
                  window.onload = function(){ setTimeout(goPrint, 300); };
                } else {
                  document.getElementById('printBtn')?.addEventListener('click', goPrint);
                }
                document.getElementById('closeBtn')?.addEventListener('click', goClose);
              })();
            </script>
          </body>
        </html>
      `;

      const w = preview || window.open('', '_blank');
      if (!w) throw new Error('Brak uprawnień do otwarcia okna');
      w.document.open();
      w.document.write(html);
      w.document.close();
    } catch (e: any) {
      alert(`Nie udało się wygenerować raportu: ${e.message || e}`);
    }
  };

  const handlePrintCareReport = async () => {
    if (!family || !user) {
      alert('Zaloguj się i wybierz rodzinę.');
      return;
    }
    const monthStr = careReportMonth;
    if (!monthStr || !/^\d{4}-\d{2}$/.test(monthStr)) return;
    const [year, month] = monthStr.split('-').map(Number);
    // Ustal liczbę dni w miesiącu i zbuduj zakres bez użycia toISOString (unikamy przesunięć strefy czasowej)
    const daysInMonth = new Date(year, month, 0).getDate();
    const startIso = `${monthStr}-01`;
    const endIso = `${monthStr}-${String(daysInMonth).padStart(2, '0')}`;

    // Otwórz okno NATYCHMIAST po kliknięciu (iOS wymaga synchronizacji z gestem)
    const preview = window.open('', '_blank');
    if (preview) {
      preview.document.open();
      preview.document.write(`<!doctype html><html lang="pl"><head><meta charset="utf-8" /><meta name="viewport" content="width=device-width, initial-scale=1" /><title>Generowanie…</title></head><body style="font-family: system-ui, -apple-system, Segoe UI, Roboto, sans-serif; padding: 16px; color:#0f172a;"><div>Generowanie raportu opieki — ${monthStr}…</div></body></html>`);
      preview.document.close();
    }

    try {
      const { data: childrenRes } = await supabase
        .from('children')
        .select('id,name')
        .eq('family_id', family.id);
      const children = (childrenRes || []) as Array<{ id: string; name: string }>;
      const childIdToName = new Map<string, string>(children.map(c => [c.id, c.name]));

      const { data: entries, error } = await supabase
        .from('calendar_entries')
        .select('id,child_id,date,location,updated_at')
        .eq('family_id', family.id)
        .gte('date', startIso)
        .lte('date', endIso);
      if (error) throw error;

      // Zliczanie dni dla każdego dziecka (z deduplikacją: 1 dzień = max 1 wpis)
      type Totals = { home_a: number; home_b: number; unassigned: number };
      const totalsByChild = new Map<string, Totals>();
      for (const c of children) {
        totalsByChild.set(c.id, { home_a: 0, home_b: 0, unassigned: daysInMonth });
      }
      // Zbuduj mapę unikalnych dni per dziecko, wybierając najnowszy wpis (po updated_at)
      const latestPerDay = new Map<string, { child_id: string; date: string; location: 'home_a'|'home_b'; updated_at?: string }>();
      for (const e of (entries || []) as Array<{ id: string; child_id: string; date: string; location: 'home_a'|'home_b'; updated_at?: string }>) {
        const key = `${e.child_id}::${e.date}`;
        const current = latestPerDay.get(key);
        if (!current) {
          latestPerDay.set(key, e);
        } else {
          // Jeśli mamy znacznik czasu, wybierz nowszy
          const currTs = current.updated_at ? Date.parse(current.updated_at) : 0;
          const newTs = e.updated_at ? Date.parse(e.updated_at) : 0;
          if (newTs >= currTs) latestPerDay.set(key, e);
        }
      }

      for (const e of latestPerDay.values()) {
        const t = totalsByChild.get(e.child_id);
        if (!t) continue;
        if (e.location === 'home_a') t.home_a += 1;
        else if (e.location === 'home_b') t.home_b += 1;
        if (t.unassigned > 0) t.unassigned -= 1;
      }

      // Korekta nieprzypisanych tak, aby zawsze: unassigned = dni_miesiąca - A - B
      for (const [childId, t] of totalsByChild.entries()) {
        t.unassigned = Math.max(0, daysInMonth - t.home_a - t.home_b);
        totalsByChild.set(childId, t);
      }

      const title = `Raport opieki — ${monthStr}`;
      const stamp = new Date().toLocaleString('pl-PL');
      const email = user?.email || '—';
      const homeALabel = `Dom A${family?.home_a_name && family.home_a_name !== 'Dom A' ? ` (${family.home_a_name})` : ''}`;
      const homeBLabel = `Dom B${family?.home_b_name && family.home_b_name !== 'Dom B' ? ` (${family.home_b_name})` : ''}`;

      const styles = `
        <style>
          @page { margin: 16mm; }
          body { font-family: 'Noto Sans', system-ui, -apple-system, Segoe UI, Roboto, sans-serif; color: #0f172a; }
          h1 { font-size: 18px; margin: 0 0 8px; }
          .meta { font-size: 12px; margin: 0 0 16px; }
          .section { margin: 16px 0; }
          .section h2 { font-size: 14px; margin: 0 0 8px; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; }
          th, td { border: 1px solid #e2e8f0; padding: 6px 8px; text-align: left; }
          th { background: #e9f0ff; }
          tfoot td { font-weight: 600; }
          .footer { position: fixed; bottom: 10mm; left: 16mm; right: 16mm; font-size: 10px; color: #475569; }
        </style>
      `;

      const toPct = (n: number) => ((n / daysInMonth) * 100).toFixed(1) + '%';
      const childBlocks = Array.from(totalsByChild.entries()).map(([childId, t]) => {
        const name = childIdToName.get(childId) || '—';
        const a = t.home_a;
        const b = t.home_b;
        const u = Math.max(0, daysInMonth - a - b);
        return `
          <table class="child">
            <thead>
              <tr><th colspan="2">${escapeHtml(name)}</th></tr>
            </thead>
            <tbody>
              <tr>
                <td>${escapeHtml(homeALabel)} (dni)</td>
                <td>${a}</td>
              </tr>
              <tr>
                <td>${escapeHtml(homeALabel)} (%)</td>
                <td>${toPct(a)}</td>
              </tr>
              <tr>
                <td>${escapeHtml(homeBLabel)} (dni)</td>
                <td>${b}</td>
              </tr>
              <tr>
                <td>${escapeHtml(homeBLabel)} (%)</td>
                <td>${toPct(b)}</td>
              </tr>
              <tr>
                <td>Nieprzypisane (dni)</td>
                <td>${u}</td>
              </tr>
              <tr>
                <td>Nieprzypisane (%)</td>
                <td>${toPct(u)}</td>
              </tr>
            </tbody>
          </table>
        `;
      }).join('');

      const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) || (navigator.maxTouchPoints || 0) > 1;
      const html = `
        <!doctype html>
        <html lang="pl">
          <head>
            <meta charset="utf-8" />
            <meta name="viewport" content="width=device-width, initial-scale=1" />
            <link href="https://fonts.googleapis.com/css2?family=Noto+Sans:wght@400;600&display=swap&subset=latin-ext" rel="stylesheet" />
            ${styles}
            <title>${title}</title>
          </head>
          <body>
            <div style="display:flex; align-items:flex-start; justify-content:space-between; gap:12px;">
              <div>
                <h1>${title}</h1>
                <div class="meta">${escapeHtml(homeALabel)}</div>
                <div class="meta">${escapeHtml(homeBLabel)}</div>
                <div class="meta">Liczba dni w miesiącu: ${daysInMonth}</div>
              </div>
              <div style="display:flex; gap:8px; align-items:center;">
                <button id="closeBtn" style="padding:8px 12px; font-size:12px; background:#fff; color:#1e293b; border:1px solid #cbd5e1; border-radius:6px;">Zamknij</button>
                <button id="printBtn" style="display:${isMobile ? 'inline-block' : 'none'}; padding:8px 12px; font-size:13px; background:#1e293b; color:#fff; border:0; border-radius:6px;">Drukuj</button>
              </div>
            </div>
            <div class="section">
              ${childBlocks}
            </div>
            <div class="footer">Raport wygenerowany z aplikacji 2domy.pl — konto: ${escapeHtml(email)} — ${stamp}</div>
            <script>
              (function(){
                var isMobile = ${isMobile ? 'true' : 'false'};
                function goPrint(){ try { window.print(); } catch(e){} }
                function goClose(){ try { window.close(); setTimeout(function(){ if (!document.hidden) { window.location.href = '/account'; } }, 50); } catch(e){ try { window.location.href = '/account'; } catch(_){} } }
                if (!isMobile) {
                  window.onload = function(){ setTimeout(goPrint, 300); };
                } else {
                  document.getElementById('printBtn')?.addEventListener('click', goPrint);
                }
                document.getElementById('closeBtn')?.addEventListener('click', goClose);
              })();
            </script>
          </body>
        </html>
      `;

      const w = preview || window.open('', '_blank');
      if (!w) throw new Error('Brak uprawnień do otwarcia okna');
      w.document.open();
      w.document.write(html);
      w.document.close();
    } catch (e: any) {
      alert(`Nie udało się wygenerować raportu: ${e.message || e}`);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmEmail !== (user?.email || '')) return;
    setIsDeletingAccount(true);
    setDeleteError('');
    try {
      await api.deleteAccount();
      // Konto usunięte po stronie serwera — czyść lokalny stan bezpośrednio
      // (nie wywołuj signOut(), bo JWT jest już unieważniony)
      useAuthStore.setState({ user: null, session: null });
      window.location.href = '/';
    } catch (err: any) {
      setDeleteError(err.message || 'Nie udało się usunąć konta');
      setIsDeletingAccount(false);
    }
  };

  return (
    <Layout>
      <div className="bg-gray-50">
        {/* Tabs */}
        <div className="bg-white border-b sticky top-0 z-10">
          <div className="px-4 py-2">
            <div className={cn('grid gap-1', isObserver ? 'grid-cols-1 max-w-[120px]' : 'grid-cols-4')}>
              {([
                { id: 'plan', label: 'Plan', icon: '🏷️' },
                { id: 'security', label: 'Hasło', icon: '🔒' },
                { id: 'history', label: 'Historia', icon: '📃' },
                { id: 'reports', label: 'Raporty', icon: '📊' },
              ] as const).filter(tab => !isObserver || tab.id === 'security').map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as AccountTab)}
                  className={cn(
                    'flex flex-col items-center justify-center p-2 rounded-lg cursor-pointer select-none',
                    'transition-colors duration-100 active:scale-95',
                    'text-xs font-medium touch-manipulation',
                    activeTab === tab.id
                      ? 'bg-blue-100 text-blue-600 border-2 border-blue-200'
                      : 'text-gray-600 hover:bg-gray-100 border-2 border-transparent'
                  )}
                >
                  <span className="text-lg mb-1">{tab.icon}</span>
                  <span className="leading-tight text-center">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="p-3">
          {/* Hasło */}
          {activeTab === 'plan' && (
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-4 border-b bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-800 flex items-center">
                  <span className="mr-2">🏷️</span>
                  Twój plan
                </h2>
              </div>
              <div className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-slate-700">
                    Status: {planId || '—'}
                    {planStatus && (
                      <span className="ml-2 text-xs text-slate-500">({planStatus})</span>
                    )}
                  </div>
                  <a href="/pricing" className="btn btn-sm btn-primary w-[7.5rem] inline-flex items-center justify-center text-center">Cennik</a>
                </div>
                <p className="text-xs text-slate-600">Zmień lub odśwież plan na stronie Cennik. Jeśli masz aktywną subskrypcję, zmiany pojawią się po chwili.</p>
              </div>
            </div>
          )}

          {/* Hasło */}
          {activeTab === 'security' && (
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-4 border-b bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-800 flex items-center">
                  <span className="mr-2">🔒</span>
                  Zmiana hasła
                </h2>
                <p className="text-sm text-gray-600 mt-1">Hasło powinno być silne i unikalne</p>
              </div>
              <div className="p-4">
                <form onSubmit={handleChangePassword}>
                  {passwordError && (
                    <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">{passwordError}</div>
                  )}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Aktualne hasło</label>
                      <input
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        disabled={isChangingPassword}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                        placeholder="Wprowadź aktualne hasło"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Nowe hasło</label>
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        disabled={isChangingPassword}
                        minLength={8}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                        placeholder="Co najmniej 8 znaków"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Powtórz nowe hasło</label>
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        disabled={isChangingPassword}
                        minLength={8}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                        placeholder="Powtórz nowe hasło"
                        required
                      />
                    </div>
                  </div>
                  <div className="pt-6 border-t mt-6">
                    <button
                      type="submit"
                      disabled={isChangingPassword || !currentPassword || !newPassword || !confirmPassword}
                      className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-lg font-medium flex items-center justify-center text-base"
                    >
                      {isChangingPassword ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                          Zmienianie hasła...
                        </>
                      ) : (
                        <>
                          <span className="mr-2">🔒</span>
                          Zmień hasło
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Historia */}
          {activeTab === 'history' && (
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-2 border-b bg-gray-50">
                <h2 className="text-base font-semibold text-gray-800 flex items-center">
                  <span className="mr-2">📃</span>
                  Historia zmian
                </h2>
              </div>
              <div className="p-2">
                <div className="mb-3">
                  <div className="flex items-center justify-between">
                    <div className="mt-2 flex items-center gap-2">
                      <label className="text-xs text-gray-600">Filtr akcji:</label>
                      <select
                        value={actionFilter}
                        onChange={(e) => setActionFilter(e.target.value)}
                        className="text-xs border rounded px-2 py-1 bg-white"
                      >
                        <option value="">Wszystkie</option>
                        <option value="calendar_entry_created">Dodano wpis w kalendarzu</option>
                        <option value="calendar_entry_updated">Zaktualizowano wpis w kalendarzu</option>
                        <option value="calendar_entry_deleted">Usunięto wpis z kalendarza</option>
                        <option value="note_created">Dodano notatkę</option>
                        <option value="note_updated">Zaktualizowano notatkę</option>
                        <option value="note_deleted">Usunięto notatkę</option>
                        <option value="expense_created">Dodano koszt</option>
                        <option value="expense_updated">Zaktualizowano koszt</option>
                        <option value="expense_deleted">Usunięto koszt</option>
                      </select>
                    </div>
                    <button onClick={() => fetchActivityLogs(historyPage)} className="text-xs text-blue-600 hover:text-blue-800 flex items-center">
                      <span className="mr-1">🔄</span>
                      Odśwież
                    </button>
                  </div>
                </div>
                {/* Prosty renderer listy */}
                <ActivityLogList activities={activityLogs} isLoading={isLoadingLogs} error={logsError} />
                {/* Paginacja dla Pro/Max */}
                {historyPlan && historyPlan !== 'basic' && historyTotal > pageSize && (
                  <div className="mt-3 flex items-center justify-between">
                    <button
                      className="px-3 py-1 text-sm rounded border disabled:opacity-50"
                      onClick={() => fetchActivityLogs(Math.max(0, historyPage - 1))}
                      disabled={historyPage === 0 || isLoadingLogs}
                    >
                      Poprzednia
                    </button>
                    <div className="text-xs text-gray-600">
                      Strona {historyPage + 1} z {Math.max(1, Math.ceil(historyTotal / pageSize))}
                    </div>
                    <button
                      className="px-3 py-1 text-sm rounded border disabled:opacity-50"
                      onClick={() => fetchActivityLogs(Math.min(historyPage + 1, Math.max(0, Math.ceil(historyTotal / pageSize) - 1)))}
                      disabled={historyPage >= Math.ceil(historyTotal / pageSize) - 1 || isLoadingLogs}
                    >
                      Następna
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Raporty */}
          {activeTab === 'reports' && (
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-4 border-b bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-800 flex items-center">
                  <span className="mr-2">📊</span>
                  Raporty
                </h2>
              </div>
              <div className="p-4 space-y-4">
                <div className="border rounded-lg p-4">
                  <div>
                    <div className="font-semibold text-gray-800">Raport kosztów</div>
                    <p className="text-sm text-gray-600 mt-1">Wygeneruj zestawienie kosztów dla wskazanego miesiąca</p>
                    <div className="mt-2 flex items-center gap-2">
                      <label className="text-xs text-gray-600" htmlFor="report-month">Miesiąc:</label>
                      <input
                        id="report-month"
                        type="month"
                        className="border rounded px-2 py-1 text-sm"
                        value={reportMonth}
                        onChange={(e) => setReportMonth(e.target.value)}
                      />
                    </div>
                    <div className="mt-3">
                      <button
                        onClick={handlePrintExpenseReport}
                        className="btn"
                        disabled={!planId || planId === 'basic'}
                        title="Wersja do druku (z polskimi znakami)"
                      >
                        {planId && planId !== 'basic' ? 'Drukuj' : 'Dostępne w planie Pro/Max'}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="border rounded-lg p-4">
                  <div>
                    <div className="font-semibold text-gray-800">Raport opieki</div>
                    <p className="text-sm text-gray-600 mt-1">Zestawienie: ile dni każdy podopieczny był w Domu A/B</p>
                    <div className="mt-2 flex items-center gap-2">
                      <label className="text-xs text-gray-600" htmlFor="care-report-month">Miesiąc:</label>
                      <input
                        id="care-report-month"
                        type="month"
                        className="border rounded px-2 py-1 text-sm"
                        value={careReportMonth}
                        onChange={(e) => setCareReportMonth(e.target.value)}
                      />
                    </div>
                    <div className="mt-3">
                      <button
                        onClick={handlePrintCareReport}
                        className="btn"
                        disabled={!planId || planId === 'basic'}
                        title="Wersja do druku (z polskimi znakami)"
                      >
                        {planId && planId !== 'basic' ? 'Drukuj' : 'Dostępne w planie Pro/Max'}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="border rounded-lg p-4">
                  <div>
                    <div className="font-semibold text-gray-800">Eksport danych (JSON)</div>
                    <p className="text-sm text-gray-600 mt-1">Pobierz kopię danych rodziny z wybranego miesiąca</p>
                    <div className="mt-2 flex items-center gap-2">
                      <label className="text-xs text-gray-600" htmlFor="export-month">Miesiąc:</label>
                      <input
                        id="export-month"
                        type="month"
                        className="border rounded px-2 py-1 text-sm"
                        value={exportMonth}
                        onChange={(e) => setExportMonth(e.target.value)}
                      />
                    </div>
                    <div className="mt-3">
                      <button
                        onClick={handleExportData}
                        disabled={isExporting || !family}
                        className="btn"
                      >
                        {isExporting ? 'Eksportowanie…' : 'Pobierz JSON'}
                      </button>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          )}

          {/* Konto */}
          {activeTab === 'account' && (
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-4 border-b bg-gray-50">
                <h2 className="text-base font-semibold text-gray-800 flex items-center">
                  <span className="mr-2">👤</span>
                  Zarządzanie kontem
                </h2>
              </div>
              <div className="p-4">
                <p className="text-sm text-gray-600 mb-2">
                  Zalogowany jako: <strong>{user?.email}</strong>
                </p>
              </div>
              {/* Strefa niebezpieczna */}
              <div className="border-t border-red-200 bg-red-50 rounded-b-lg p-4">
                <h3 className="text-sm font-semibold text-red-800 mb-1">Strefa niebezpieczna</h3>
                <p className="text-xs text-red-700 mb-3">
                  Usunięcie konta jest nieodwracalne. Zostaną usunięte wszystkie Twoje dane,
                  w tym historia kalendarza, dzieci i rodziny, gdzie jesteś jedynym członkiem.
                </p>
                <button
                  onClick={() => { setIsDeleteModalOpen(true); setDeleteConfirmEmail(''); setDeleteError(''); }}
                  className="bg-red-600 hover:bg-red-700 text-white text-sm px-4 py-2 rounded-lg font-medium"
                >
                  Usuń konto
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal potwierdzenia usunięcia konta */}
      {isDeleteModalOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={() => !isDeletingAccount && setIsDeleteModalOpen(false)}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && !isDeletingAccount) setIsDeleteModalOpen(false);
          }}
          tabIndex={-1}
        >
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-red-700 mb-2">Usuń konto</h3>
            <p className="text-sm text-gray-700 mb-1">
              Ta operacja jest <strong>nieodwracalna</strong>. Wszystkie Twoje dane zostaną trwale usunięte.
            </p>
            <p className="text-sm text-gray-700 mb-4">
              Wpisz swój adres email (<strong>{user?.email}</strong>) aby potwierdzić:
            </p>
            <input
              type="email"
              value={deleteConfirmEmail}
              onChange={(e) => setDeleteConfirmEmail(e.target.value)}
              placeholder={user?.email || ''}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-red-400"
              disabled={isDeletingAccount}
            />
            {deleteError && (
              <p className="text-xs text-red-600 mb-3">{deleteError}</p>
            )}
            <div className="flex gap-2">
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                disabled={isDeletingAccount}
                className="flex-1 border border-gray-300 text-gray-700 text-sm px-4 py-2 rounded-lg hover:bg-gray-50"
              >
                Anuluj
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={isDeletingAccount || deleteConfirmEmail !== (user?.email || '')}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white text-sm px-4 py-2 rounded-lg font-medium"
              >
                {isDeletingAccount ? 'Usuwanie…' : 'Usuń konto'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default Account;


